const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface ProductionSheetClosureState {
  activeCommandId: string | null;
  hasVersionedEventHistory: boolean;
  draftClosed: boolean;
  draftStockCommandId: string | null | undefined;
}

/**
 * Once a draft is bound to a StockCommand, append-only command history owns
 * its state. `draft.closed` is only a legacy fallback: a terminal server
 * rejection deletes the optimistic command events, so activeCommandId becomes
 * null and the sheet must reopen even after an app restart.
 */
export function productionSheetIsClosed(
  state: ProductionSheetClosureState,
): boolean {
  const hasDurableCommandLineage = state.hasVersionedEventHistory
    || (
      typeof state.draftStockCommandId === 'string'
      && UUID_PATTERN.test(state.draftStockCommandId)
    );
  return hasDurableCommandLineage
    ? state.activeCommandId !== null
    : state.draftClosed;
}

/**
 * A persisted closed draft with command lineage but no surviving append-only
 * command facts means that the optimistic discharge was rolled back (normally
 * after a terminal server rejection). Surface this as an actionable incident,
 * never as a silently reopened ordinary chef draft.
 */
export function productionSheetNeedsDischargeReconciliation(
  state: ProductionSheetClosureState,
): boolean {
  return state.draftClosed
    && typeof state.draftStockCommandId === 'string'
    && UUID_PATTERN.test(state.draftStockCommandId)
    && state.activeCommandId === null
    && !state.hasVersionedEventHistory;
}
